import { Reference, useApolloClient } from "@apollo/client";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Alert,
  AlertTitle,
  Box,
  type BoxProps,
  Button,
  IconButton,
  Stack as MuiStack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useRef } from "react";
import {
  AnnotationSeverity,
  PermissionName,
} from "../../../clients/graphqlTypes.js";
import { RestrictedByDocumentPermissionWithDebug } from "../../auth/RestrictedByDocumentPermission.js";
import {
  ConfirmProcess,
  ConfirmProcessRef,
} from "../../commons/modals/ConfirmProcess.js";
import { useProjectQuoteContext } from "../../main-routes/projects/ProjectQuoteContext.js";
import {
  QuoteAnnotationFragment,
  useDiscardQuoteAnnotationMutation,
} from "./QuoteAnnotations.generated.js";

const SEVERITY_TO_MUI: Record<
  AnnotationSeverity,
  "info" | "warning" | "success" | "error"
> = {
  INFO: "info",
  WARNING: "warning",
  SUCCESS: "success",
  ERROR: "error",
};

export const QuoteAnnotationsBanner = (props?: Omit<BoxProps, "children">) => {
  const { t } = useTranslate(["Quote", "Global"]);
  const confirmProcessRef = useRef<ConfirmProcessRef>(null);
  const {
    projectId,
    quoteId,
    viewerPermissions,
    quoteAnnotations,
    dismissAnnotation,
    hiddenAnnotationCount,
    restoreAnnotations,
  } = useProjectQuoteContext();

  if (quoteAnnotations.length === 0 && hiddenAnnotationCount === 0) return null;

  return (
    <Box {...props}>
      <MuiStack direction="column" spacing={1}>
        {quoteAnnotations.map(annotation => (
          <QuoteAnnotationAlert
            key={annotation.id}
            annotation={annotation}
            projectId={projectId}
            quoteId={quoteId}
            viewerPermissions={viewerPermissions}
            confirmProcessRef={confirmProcessRef}
            onDismiss={dismissAnnotation}
          />
        ))}
        {hiddenAnnotationCount > 0 && (
          <Button
            size="small"
            variant="text"
            startIcon={<VisibilityOutlinedIcon fontSize="small" />}
            onClick={restoreAnnotations}
          >
            {t("Show {count} hidden notes", {
              ns: "Quote",
              count: hiddenAnnotationCount,
            })}
          </Button>
        )}
      </MuiStack>
      <ConfirmProcess ref={confirmProcessRef} />
    </Box>
  );
};

const QuoteAnnotationAlert = ({
  annotation,
  projectId,
  quoteId,
  viewerPermissions,
  confirmProcessRef,
  onDismiss,
}: {
  annotation: QuoteAnnotationFragment;
  projectId: string;
  quoteId: string;
  viewerPermissions: PermissionName[];
  confirmProcessRef: React.RefObject<ConfirmProcessRef>;
  onDismiss: (annotationId: string) => void;
}) => {
  const { t } = useTranslate(["Quote", "Global"]);
  const { enqueueSnackbar } = useSnackbar();
  const client = useApolloClient();

  const [discardQuoteAnnotation] = useDiscardQuoteAnnotationMutation({
    client,
  });

  const handleDiscard = async () => {
    const confirmed = await confirmProcessRef.current?.startConfirmProcess({
      title: t("Discard this note?", { ns: "Quote" }),
      text: t(
        "This action cannot be undone. The note will be hidden permanently.",
        { ns: "Quote" }
      ),
      confirmButtonLabel: t("Discard", { ns: "Global" }),
    });
    if (!confirmed) return;

    try {
      await discardQuoteAnnotation({
        variables: {
          input: { projectId, quoteId, annotationId: annotation.id },
        },
        update: cache => {
          cache.modify<{ annotations: Reference[] }>({
            id: cache.identify({ __typename: "Quote", id: quoteId }),
            fields: {
              annotations(existing = [], { readField }) {
                return existing.filter(
                  ref => readField("id", ref) !== annotation.id
                );
              },
            },
          });
        },
      });
    } catch (e) {
      if (e instanceof Error) {
        enqueueSnackbar(e.message, { variant: "error" });
      }
    }
  };

  const author =
    `${annotation.createdBy.firstname} ${annotation.createdBy.familyname}`.trim();
  const when = moment(annotation.createdAt).format("LL");

  return (
    <Alert
      severity={SEVERITY_TO_MUI[annotation.severity]}
      sx={{ "& .MuiAlert-message": { flex: 1 } }}
      action={
        <MuiStack direction="row" spacing={0.5}>
          <RestrictedByDocumentPermissionWithDebug
            permission="MANAGE_QUOTES"
            document={{ viewerPermissions }}
          >
            <Tooltip title={t("Discard", { ns: "Global" })}>
              <IconButton
                size="small"
                color="primary"
                aria-label={t("Discard", { ns: "Global" })}
                onClick={handleDiscard}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </RestrictedByDocumentPermissionWithDebug>
          <Tooltip title={t("Close", { ns: "Global" })}>
            <IconButton
              size="small"
              color="primary"
              aria-label={t("Close", { ns: "Global" })}
              onClick={() => onDismiss(annotation.id)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </MuiStack>
      }
    >
      {annotation.title ? <AlertTitle>{annotation.title}</AlertTitle> : null}
      {annotation.body}
      <Typography
        variant="caption"
        sx={{ display: "block", mt: 0.5, textAlign: "right" }}
      >
        {author ? `${author} · ${when}` : when}
      </Typography>
    </Alert>
  );
};
